'use client';

import { FunctionComponent, use } from 'react';
import Head from 'next/head';

import { PageContext } from '@/react/components/templates/context/PageContext';
import { useLocale } from '@/react/hooks/useLocale';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { DYPageType } from '@/services/serverless/integrations/DYService/DYPageType';
import { DYPageTypeMap } from '@/services/serverless/integrations/DYService/DYPageTypeMap';
import { isNullish } from '@/utils/null-utils';

import ScriptLoader from '../../utility/ScriptLoader';

/**
 * Adds the dynamic yield scripts to manage dynamic yield analytics.
 *
 * All scripts are loaded before the page is interactive and are not async,
 * to follow documentation recommendations.
 *
 * See https://dy.dev/docs/implement-script#best-practices for more information.
 *
 * At least one DY context needs to be set before the DY API is loaded in order to load the correct data.
 * The initial context type is derived from the current page when this component is rendered inside
 * {@link PageProvider} (e.g. under {@link PageFromPathProvider}); otherwise it falls back to "OTHER".
 *
 * See https://dy.dev/docs/code-context and https://dy.dev/docs/script-overview for more information.
 */
const DynamicYield: FunctionComponent = () => {
  const { language, country } = useLocale();
  const config = ConfigurationService.getConfig(
    'dynamicYield',
    language,
    country
  );

  const pageContext = use(PageContext);
  const pageType = pageContext?.page?.pageType;
  const dyPageType = isNullish(pageType)
    ? DYPageType.Other
    : DYPageTypeMap[pageType];

  const isDYEnabled = config.getSetting('isDYEnabled').value;
  const isDYClientEnabled = config.getSetting('isDYClientEnabled').value;
  const siteID = config.getSetting('siteID').value;
  const setInitialDYContext = config.getSetting('setInitialDYContext').value;

  return (
    <>
      {isDYEnabled && isDYClientEnabled && (
        <>
          <Head>
            <link rel="preconnect" href="//cdn.dynamicyield.com" />
            <link rel="preconnect" href="//st.dynamicyield.com" />
            <link rel="preconnect" href="//rcom.dynamicyield.com" />
            <link rel="dns-prefetch" href="//cdn.dynamicyield.com" />
            <link rel="dns-prefetch" href="//st.dynamicyield.com" />
            <link rel="dns-prefetch" href="//rcom.dynamicyield.com" />
          </Head>
          {setInitialDYContext === true && (
            <ScriptLoader
              async={false}
              strategy="beforeInteractive"
              type="text/javascript"
            >
              {`window.DY=window.DY||{},window.DY.recommendationContext||(window.DY.recommendationContext={type:"${dyPageType}"});`}
            </ScriptLoader>
          )}
          <ScriptLoader
            async={false}
            strategy="beforeInteractive"
            type="text/javascript"
            src={`//cdn.dynamicyield.com/api/${siteID}/api_static.js`}
          />
          <ScriptLoader
            async={false}
            strategy="beforeInteractive"
            type="text/javascript"
            src={`//cdn.dynamicyield.com/api/${siteID}/api_dynamic.js`}
          />
        </>
      )}
    </>
  );
};

export default DynamicYield;
